import { PureComponent } from 'react';

import {
  Header,
  PrimaryButton,
  Select,
  Spacer,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TertiaryButton,
  Text,
  Toggle,
  Toolbar,
  WithTooltip,
} from '@procurify/ui'
import { isUndefined } from 'lodash'
import { FormattedMessage } from 'react-intl'

import { FormattedEnhancedMessage } from '@webapp/core/hoc/FormattedEnhancedMessage'
import { ImporterPreviewStageStyled } from './styles/ImporterPreviewStageStyled'
import { ImporterPreviewTableStyled } from './styles/ImporterPreviewTableStyled'
import messages from '../../messages'
import { ImporterContentStyled as Content } from '../../styles/ImporterContentStyled'
import {
  type ImporterColumnMap,
  type ImporterFieldMatch,
  type ImporterFieldNameMap,
  type ImporterOptionalFields,
  type ImporterRequiredFields,
  type ImporterSampleData,
} from '../../types'
import { ImporterUploadStageStyled } from '../ImporterUploadStage/styles/ImporterUploadStageStyled'

export interface IImporterPreviewStageProps {
  fieldNameMapper?: ImporterFieldNameMap
  fieldMatcher?: ImporterFieldMatch
  requiredFields?: ImporterRequiredFields
  optionalFields?: ImporterOptionalFields
  sampleData?: ImporterSampleData
  onConfirm?: (payload: {
    mappedColumns: ImporterColumnMap
    ignoreFirstRow: boolean
  }) => void
  onDismiss?: () => void
  isButtonLoading?: boolean
}

export interface IImporterPreviewStageState {
  ignoreFirstRow: boolean
  mappedColumns: ImporterColumnMap
  mappedColumnsByIndex: { [key: string]: string }
}

const initialState = {
  ignoreFirstRow: false,
  mappedColumns: {},
  mappedColumnsByIndex: {},
}

export class ImporterPreviewStage extends PureComponent<
  IImporterPreviewStageProps,
  IImporterPreviewStageState
> {
  static readonly defaultProps = {
    fieldNameMapper: {},
    fieldMatcher: {},
    requiredFields: [],
    optionalFields: [],
    sampleData: {
      rows: [],
    },
    isButtonLoading: false,
  }

  readonly state = initialState

  modalRef = null

  componentDidMount() {
    this.processSampleData()
  }

  getHeaderRow = () => {
    return this.props.sampleData.rows.slice(0, 1)[0] || []
  }

  getDataRows = () => {
    if (!this.props.sampleData?.rows.length) return []

    const sampleData = this.props.sampleData.rows
      .slice(this.state.ignoreFirstRow ? 1 : 0)
      .slice(0, 5)
    const numCols = this.props.sampleData.rows[0].length

    if (sampleData.length < 5) {
      const numPlaceholders = 5 - sampleData.length
      for (let i = 0; i < numPlaceholders; i++) {
        sampleData.push(new Array(numCols).fill('...'))
      }
    }

    return sampleData
  }

  isValidFieldMatch = (value: string, csvFields: string[]) => {
    const { fieldNameMapper, fieldMatcher } = this.props

    const sanitizedValue = value.replace('*', '').toLowerCase().trim()

    const isExactMatch = csvFields.find(
      (csvField) => csvField.toLowerCase().trim() === sanitizedValue
    )

    if (isExactMatch) return isExactMatch

    const matchAliases = csvFields.find((csvField) => {
      // Check field name mapper first
      if (fieldNameMapper[csvField]) {
        if (fieldNameMapper[csvField].toLowerCase().trim() === sanitizedValue) {
          return csvField
        }
      }

      if (!fieldMatcher || !fieldMatcher[csvField]) return

      const aliases = fieldMatcher[csvField].map((alias) => {
        if (typeof alias === 'string') {
          return alias.toLowerCase().trim()
        }

        return alias
      })

      return aliases.find((alias) => {
        if (alias === sanitizedValue) {
          return csvField
        }

        if (!!sanitizedValue.match(alias)) {
          return csvField
        }
      })
    })

    if (matchAliases) return matchAliases

    return false
  }

  processSampleData = () => {
    const { sampleData } = this.props

    if (!sampleData) return

    const headerRow = sampleData.rows[0]

    if (!headerRow) return

    const csvFields = [
      ...this.props.requiredFields,
      ...this.props.optionalFields,
    ]

    const mappedColumns = {}
    const mappedColumnsByIndex = {}
    let numMappedColumns = 0

    headerRow.forEach((cellValue, colIndex) => {
      if (csvFields.length <= 0) return

      const matchedCSVField = this.isValidFieldMatch(cellValue, csvFields)

      if (matchedCSVField) {
        csvFields.splice(csvFields.indexOf(matchedCSVField), 1)
        mappedColumns[matchedCSVField] = colIndex
        mappedColumnsByIndex[colIndex] = matchedCSVField

        numMappedColumns++
      }
    })

    this.setState({
      mappedColumns,
      mappedColumnsByIndex,
      ignoreFirstRow:
        numMappedColumns >=
        this.props.requiredFields.length + this.props.optionalFields.length,
    })
  }

  buildSelectOptions = () => {
    const requiredOptions = {
      label: 'Required Fields',
      options: this.props.requiredFields
        .filter((field) => {
          return isUndefined(this.state.mappedColumns[field])
        })
        .map((field) => ({
          value: field,
          label: this.getFieldLabel(field, this.props.fieldNameMapper),
        })),
    }

    const optionalOptions = {
      label: 'Optional Fields',
      options: this.props.optionalFields
        .filter((field) => {
          return isUndefined(this.state.mappedColumns[field])
        })
        .map((field) => ({
          value: field,
          label: this.getFieldLabel(field, this.props.fieldNameMapper),
        })),
    }

    return [requiredOptions, optionalOptions]
  }

  onMappingSelect = (value, colIndex) => {
    this.setState((state) => ({
      mappedColumns: {
        ...state.mappedColumns,
        [value.value]: colIndex,
      },
      mappedColumnsByIndex: {
        ...state.mappedColumnsByIndex,
        [colIndex]: value.value,
      },
    }))
  }

  unmapColumn = (colIndex) => {
    this.setState((state) => {
      const currentMappedValue = state.mappedColumnsByIndex[colIndex]

      return {
        mappedColumns: {
          ...state.mappedColumns,
          [currentMappedValue]: undefined,
        },
        mappedColumnsByIndex: {
          ...state.mappedColumnsByIndex,
          [colIndex]: undefined,
        },
      }
    })
  }

  getFieldLabel = (key, mapper) => {
    return mapper[key] ? mapper[key] : key
  }

  hasValidMappings = () => {
    return !this.props.requiredFields.find((field) => {
      return isUndefined(this.state.mappedColumns[field])
    })
  }

  getMissingFields = () => {
    return this.props.requiredFields.filter((field) => {
      return isUndefined(this.state.mappedColumns[field])
    })
  }

  formatMissingFields = (fields: string[]) => {
    let missingFieldsString = ''

    fields.forEach((field, index) => {
      const fieldLabel = this.getFieldLabel(field, this.props.fieldNameMapper)
      missingFieldsString = missingFieldsString.concat(fieldLabel)
      if (index < fields.length - 1) {
        missingFieldsString = missingFieldsString.concat(', ')
      }
    })

    return missingFieldsString
  }

  onIgnoreFirstRowToggle = () => {
    this.setState((state) => ({
      ignoreFirstRow: !state.ignoreFirstRow,
    }))
  }

  submitImport = () => {
    if (!this.props.onConfirm) return

    this.props.onConfirm({
      mappedColumns: this.state.mappedColumns,
      ignoreFirstRow: this.state.ignoreFirstRow,
    })
  }

  render() {
    return (
      <Content>
        <ImporterPreviewStageStyled>
          <Header as='h3' gutter>
            <FormattedMessage {...messages.previewStepTitle} />
          </Header>

          <ImporterUploadStageStyled>
            <Text>
              <FormattedMessage {...messages.previewStepDescription} />
            </Text>
          </ImporterUploadStageStyled>

          <Spacer space={2} />

          <Toolbar mb={1}>
            <Toolbar.Fill />
            <Toolbar.Item>
              <label htmlFor='ignore-first-row'>
                <Text inline>
                  <FormattedMessage {...messages.ignoreFirstRow} />
                </Text>{' '}
                <Toggle
                  id='ignore-first-row'
                  checked={this.state.ignoreFirstRow}
                  onClick={this.onIgnoreFirstRowToggle}
                />
              </label>
            </Toolbar.Item>
          </Toolbar>

          <ImporterPreviewTableStyled>
            <div className='importer-preview-table'>
              <Table>
                <TableHead>
                  <TableRow>
                    {this.getHeaderRow().map((col, colIndex) => {
                      return (
                        <TableCell key={colIndex} style={{ minWidth: 100 }}>
                          {isUndefined(
                            this.state.mappedColumnsByIndex[colIndex]
                          ) ? (
                            <div style={{ minWidth: 150 }}>
                              <Select
                                name={`column-${colIndex}`}
                                placeholder='Select a field'
                                options={this.buildSelectOptions()}
                                menuPortalTarget={document.querySelector(
                                  '#root'
                                )}
                                onChange={(value) => {
                                  this.onMappingSelect(value, colIndex)
                                }}
                              />
                            </div>
                          ) : (
                            <WithTooltip title='Click to unselect'>
                              {
                                <span
                                  className='mapped-column'
                                  onClick={() => this.unmapColumn(colIndex)}
                                >
                                  {this.getFieldLabel(
                                    this.state.mappedColumnsByIndex[colIndex],
                                    this.props.fieldNameMapper
                                  ).toUpperCase()}
                                </span>
                              }
                            </WithTooltip>
                          )}
                        </TableCell>
                      )
                    })}
                  </TableRow>
                </TableHead>
                <TableBody hoverable={false}>
                  {this.getDataRows().map((rows, rowIndex) => {
                    return (
                      <TableRow key={rowIndex}>
                        {rows.map((col, colIndex) => {
                          return <TableCell key={colIndex}>{col}</TableCell>
                        })}
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
            <div className='importer-preview-overlay'>
              <FormattedMessage {...messages.previewStepOverlayText} />
            </div>
          </ImporterPreviewTableStyled>

          <Spacer space={2} />

          <Toolbar>
            <Toolbar.Fill />
            <Toolbar.Item>
              <TertiaryButton onClick={this.props.onDismiss}>
                <FormattedMessage {...messages.previewStepBackButton} />
              </TertiaryButton>
              <WithTooltip
                title={
                  <FormattedEnhancedMessage
                    {...messages.missingColumnsTooltip}
                    values={{
                      fields: this.formatMissingFields(this.getMissingFields()),
                      numFields: this.getMissingFields().length,
                    }}
                  />
                }
                placement='top'
                isDisabled={this.hasValidMappings()}
              >
                <span className='submit-btn-wrapper'>
                  <PrimaryButton
                    disabled={!this.hasValidMappings()}
                    onClick={this.submitImport}
                    isLoading={this.props.isButtonLoading}
                  >
                    <FormattedMessage {...messages.previewStepImportButton} />
                  </PrimaryButton>
                </span>
              </WithTooltip>
            </Toolbar.Item>
          </Toolbar>
        </ImporterPreviewStageStyled>
      </Content>
    )
  }
}
